/**
 * My tasks (approved mockup 2026-10-26, v4): a to-do list. Grouped by when (Overdue, Today,
 * This week, Later); each row has a box that ticks itself when the work is done and a button that
 * names the job (Approve, Read, Answer...). Every kind of task has one icon, always the same; the
 * tile color is the part it belongs to and the date chip shows urgency. The list and the sidebar
 * count both follow the company picked in the sidebar. Notification choices open in a side panel.
 */
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, type TaskItem } from "../lib/api";
import { useStore } from "../lib/store";
import { useAuth } from "../auth/AuthProvider";
import { coName } from "../lib/brand";
import { fmt } from "../lib/format";
import { Icon, type IconName } from "../components/Icon";
import { Empty, Loading } from "../components/bits";
import { SeeHow } from "../components/SeeHow";

export const TASK_ICON: Record<string, IconName> = {
  approve: "ok", review: "ok", draft: "pen", fix: "pen", ack: "book", review_due: "clock", suggestion: "pen",
  stale_client: "user", warranty: "shield", agreement_deadline: "cal", agreement_reminder: "cal",
  ssl_expiry: "lock", domain_expiry: "lock", checklist: "list", workflow: "flow", policy_review: "clock", admin_setup: "cog", integration_error: "sync", m365_secret: "lock",
  review_book: "cal", review_prepare: "chart", review_gates: "flag", review_pitch: "eye", review_deliver: "dl", review_owners: "list", review_checkin: "chart",
  review_commitment: "tasks", review_commitment_late: "flag",
  vendor_evidence: "box", vendor_review: "box", vendor_contract: "cal",
};
/** The button names the job. */
const VERB: Record<string, string> = {
  approve: "Approve", review: "Review", draft: "Continue", fix: "Fix it", ack: "Read", review_due: "Review", suggestion: "Answer",
  stale_client: "Verify", warranty: "See devices", agreement_deadline: "Open agreement", agreement_reminder: "Open agreement",
  ssl_expiry: "See domain", domain_expiry: "See domain", checklist: "Continue", workflow: "Continue", policy_review: "Review", admin_setup: "Getting started", integration_error: "Fix the sync", m365_secret: "Replace secret",
  review_book: "Book it", review_prepare: "Prepare", review_gates: "Fix", review_pitch: "Read it", review_deliver: "Give it", review_owners: "Finish", review_checkin: "Check in",
  review_commitment: "Open", review_commitment_late: "Open",
  vendor_evidence: "Open vendor", vendor_review: "Review", vendor_contract: "Open vendor",
};
const PART: Record<TaskItem["part"], string> = { vault: "Vault", clienthub: "TechHub", reviewhub: "ReviewHub", vendorhub: "VendorHub", account: "Setup" };
type When = "overdue" | "today" | "week" | "later";
const WHEN: [When, string][] = [["overdue", "Overdue"], ["today", "Today"], ["week", "This week"], ["later", "Later"]];
const whenOf = (t: TaskItem): When =>
  t.days !== null && t.days < 0 ? "overdue" : t.days === 0 || (t.days === null && t.priority === "high") ? "today" : t.days !== null && t.days <= 7 ? "week" : "later";

/** The date chip: red today or overdue, amber within 14 days, grey later. */
export function dueChip(t: Pick<TaskItem, "due" | "days">) {
  if (!t.due || t.days === null) return null;
  const d = t.days;
  const text = d < 0 ? `${-d} day${d === -1 ? "" : "s"} late` : d === 0 ? "today" : d === 1 ? "tomorrow" : d <= 6
    ? new Date(`${t.due}T12:00:00`).toLocaleDateString("en-US", { weekday: "short" }) : fmt(t.due).replace(/, \d{4}$/, "");
  return <span className={`tk-due ${d <= 0 ? "tk-red" : d <= 14 ? "tk-amber" : "tk-grey"}`}>{text}</span>;
}

export function TasksPage() {
  const { ws, tasks, tasksDone, reloadTasks, reloadOnboarding } = useStore();
  const [showDone, setShowDone] = useState(false);
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [part, setPart] = useState<"all" | TaskItem["part"]>("all");
  const notifyOpen = sp.get("notify") === "1";
  const setNotify = (v: boolean) => {
    setSp(v ? { notify: "1" } : {}, { replace: true });
    if (v) void api.setOnboarding({ seen: "notify" }).then(() => reloadOnboarding());
  };
  useEffect(() => { void reloadTasks(); if (notifyOpen) void api.setOnboarding({ seen: "notify" }).then(() => reloadOnboarding()); }, []);
  const inWs = useMemo(() => (tasks ?? []).filter((t) => ws === "ALL" || !t.company || t.company === ws || t.company === "CPY"), [tasks, ws]);
  // In "All my companies", one setup row instead of one per company.
  const setups = inWs.filter((t) => t.kind === "admin_setup");
  const merged = setups.length > 1
    ? [...inWs.filter((t) => t.kind !== "admin_setup"), { ...setups[0]!, key: "setup:all", company: null, title: `Finish setting up ${setups.length} companies`,
        detail: setups.map((x) => `${coName(x.company ?? "")}: ${x.detail.replace(/ setup steps done$/, "")}`).join(" · "), url: "/welcome" }]
    : inWs;
  const shown = merged.filter((t) => part === "all" || t.part === part);
  const count = (pt: TaskItem["part"]) => merged.filter((t) => t.part === pt).length;
  const overdue = inWs.filter((t) => whenOf(t) === "overdue").length;

  const row = (t: TaskItem) => (
    <li key={t.key} className="t2-row">
      <span className="t2-box" title="Ticks itself when the work is done" aria-hidden="true" />
      <span className={`tk-ic tk-ic-${t.part}`}><Icon name={TASK_ICON[t.kind] ?? "tasks"} /></span>
      <div><b>{t.title}</b><div className="cap">{t.detail}{ws === "ALL" && t.company && t.company !== "CPY" && t.kind !== "admin_setup" ? ` · ${coName(t.company)}` : ""}</div></div>
      {dueChip(t) ?? <span />}
      <button className="btn btn-ghost btn-sm" onClick={() => nav(t.url)}>{VERB[t.kind] ?? "Open"}</button>
    </li>
  );

  return (
    <div className="view">
      <div className="hgroup">
        <div>
          <div className="eyebrow">{ws === "ALL" ? "All my companies" : coName(ws)}</div>
          <h1 className="title">My tasks</h1>
          <div className="lede">{tasks === null ? "Everything waiting on you." : inWs.length
            ? `${merged.length} thing${merged.length === 1 ? "" : "s"} waiting on you.${overdue ? ` ${overdue} ${overdue === 1 ? "is" : "are"} overdue.` : ""}` : "Nothing is waiting on you."}</div>
          <div className="lede-help"><SeeHow video="acknowledge" label="See how to acknowledge a policy" /></div>
        </div>
        <div className="row-btns">
          {inWs.length > 0 && <div className="seg" role="tablist">
            <button className={part === "all" ? "on" : ""} onClick={() => setPart("all")}>All {merged.length}</button>
            {(["vault", "clienthub", "reviewhub", "vendorhub", "account"] as const).filter((pt) => count(pt) > 0).map((pt) => (
              <button key={pt} className={part === pt ? "on" : ""} onClick={() => setPart(pt)}>{PART[pt]} {count(pt)}</button>
            ))}
          </div>}
          <button className="btn btn-ghost" onClick={() => setNotify(true)} title="How you hear about tasks"><Icon name="bell" size={16} />Notifications</button>
        </div>
      </div>
      {tasks === null ? <Loading /> : !shown.length ? (
        <div className="card sect"><Empty title="You're all caught up">Approvals, documents to acknowledge, reminders and checklist steps show up here when they need you.</Empty></div>
      ) : (
        <div className="card t2-card">
          {WHEN.map(([w, label]) => {
            const list = shown.filter((t) => whenOf(t) === w);
            return list.length > 0 && <div key={w}><h3 className={`t2-h ${w === "overdue" ? "t2-bad" : ""}`}>{label} <span>{list.length}</span></h3><ul className="t2-list">{list.map(row)}</ul></div>;
          })}
          {tasksDone.length > 0 && <DoneRow list={tasksDone} open={showDone} toggle={() => setShowDone((v) => !v)} />}
        </div>
      )}
      {!!tasks && !shown.length && tasksDone.length > 0 && <div className="card t2-card" style={{ marginTop: 14 }}><DoneRow list={tasksDone} open={showDone} toggle={() => setShowDone((v) => !v)} /></div>}
      {notifyOpen && <NotifyPanel onClose={() => setNotify(false)} />}
    </div>
  );
}

/** Done this week: recorded when each task left the list (Bill 2026-10-26). */
function DoneRow({ list, open, toggle }: { list: { title: string; url: string; done_at: string }[]; open: boolean; toggle: () => void }) {
  const nav = useNavigate();
  return (
    <>
      <div className="t2-done"><span className="t2-box on"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg></span>
        <span className="cap">Done this week: {list.length}{!open && ` · ${list.slice(0, 2).map((x) => x.title).join(", ")}${list.length > 2 ? `, and ${list.length - 2} more` : ""}`}</span>
        <a className="doclink" onClick={toggle}>{open ? "Hide" : "Show"}</a></div>
      {open && <ul className="t2-list">{list.map((x, i) => (
        <li key={i} className="t2-row" style={{ opacity: 0.75 }}><span className="t2-box on"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg></span><span />
          <div><b style={{ fontWeight: 600 }}>{x.title}</b><div className="cap">Done {fmt(x.done_at)}</div></div><span /><a className="doclink" onClick={() => nav(x.url)}>Open</a></li>))}</ul>}
    </>
  );
}

function NotifyPanel({ onClose }: { onClose: () => void }) {
  const { me, toast } = useStore();
  const { refresh } = useAuth();
  const n0: Partial<Record<string, unknown>> = me.prefs.notify ?? {};
  const [n, setN] = useState({ task_reminders: n0.task_reminders !== false, daily_summary: n0.daily_summary === true });
  useEffect(() => { const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); }; document.addEventListener("keydown", k); return () => document.removeEventListener("keydown", k); }, []);
  const save = async (k: keyof typeof n, v: boolean) => {
    const prev = n;
    setN({ ...n, [k]: v });
    try { await api.updateMe({ notify: { [k]: v } }); toast("Saved."); void refresh(); } catch (e) { setN(prev); toast((e as Error).message); }
  };
  return createPortal(
    <>
      <div className="t2-scrim" onClick={onClose} />
      <aside className="t2-drawer card sect" role="dialog" aria-label="How you hear about tasks">
        <div className="ck-top"><h3 style={{ margin: 0 }}><Icon name="bell" size={16} /> How you hear about tasks</h3><button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button></div>
        <div className="setrow" style={{ marginTop: 10 }}><div className="tx"><b>Email for each new task</b><span>Approvals, documents to read, reminder dates. <Link className="doclink" to="/profile?tab=notifications">Choose which kinds</Link></span></div></div>
        <div className="setrow"><div className="tx"><b>Reminder if it waits</b><span>3 days after it shows up, then once a week until it's done.</span></div>
          <label className="switch"><input type="checkbox" checked={n.task_reminders} onChange={(e) => void save("task_reminders", e.target.checked)} aria-label="Reminder if it waits" /><span /></label></div>
        <div className="setrow"><div className="tx"><b>Morning summary</b><span>One email about 8 AM Eastern listing everything, instead of separate reminders. Skipped on days with nothing new.</span></div>
          <label className="switch"><input type="checkbox" checked={n.daily_summary} onChange={(e) => void save("daily_summary", e.target.checked)} aria-label="Morning summary" /><span /></label></div>
        <div className="setrow"><div className="tx"><b>Slack or Teams</b><span>The same notices as a direct message.</span></div><span className="pill draft sm">Coming later</span></div>
      </aside>
    </>,
    document.body,
  );
}
